import axios from "axios";
import {
  ADMIN_API_URL,
  SERVICE_API_URL,
  EXTENSION_IDENTIFIER,
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
} from "../config/serviceConstant";

// Base URL for settings CRUD
const SETTINGS_URL = `${ADMIN_API_URL}/db/planetscale/clients/settings`;

// Build standard headers for setting API calls
const buildHeaders = ({ orgId, apiKey, dataCenterUrl, shop }) => ({
  orgid: orgId,
  apikey: apiKey,
  datacenterurl: dataCenterUrl,
  connname: EXTENSION_IDENTIFIER,
  [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(shop),
});

// Tolerant extraction — backends commonly wrap arrays under data/settings/records/results
const extractList = (resp) => {
  const d = resp?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.settings)) return d.settings;
  if (Array.isArray(d?.records)) return d.records;
  if (Array.isArray(d?.results)) return d.results;
  if (Array.isArray(d?.rows)) return d.rows;
  return [];
};

// Same for single-item extraction
const extractItem = (resp) => {
  const d = resp?.data;
  if (!d) return null;
  if (d?.data && typeof d.data === "object") return d.data;
  if (d?.setting && typeof d.setting === "object") return d.setting;
  return d;
};

// ── READ ──

// GET all settings for the client
export const getAllSetting = async (auth) => {
  try {
    const resp = await axios.request({
      url: SETTINGS_URL,
      method: "GET",
      headers: buildHeaders(auth),
    });
    return extractList(resp);
  } catch (error) {
    console.error("getAllSetting error:", error);
    return [];
  }
};

// GET a single setting by id
export const getSettingById = async (auth, settingId) => {
  try {
    const resp = await axios.request({
      url: `${SETTINGS_URL}/${settingId}`,
      method: "GET",
      headers: buildHeaders(auth),
    });
    return extractItem(resp);
  } catch (error) {
    console.error("getSettingById error:", error);
    return null;
  }
};

// GET settings filtered by type (e.g. "systemDefined")
// Strategy: fetch all and filter client-side — backend may not honour the query param.
export const getSettingByType = async (auth, settingType) => {
  try {
    const resp = await axios.request({
      url: SETTINGS_URL,
      method: "GET",
      headers: buildHeaders(auth),
      params: { settingType },
    });
    const all = extractList(resp);
    if (!settingType) return all;
    // Tolerant of camelCase / snake_case from backend
    return all.filter((s) => {
      const t = s?.settingType ?? s?.setting_type ?? s?.type;
      return !t || t === settingType;
    });
  } catch (error) {
    console.error("getSettingByType error:", error);
    return [];
  }
};

// GET the related-list setting for a given Zoho module. The backend stores the
// per-module Shopify ID / Shop URL field names inside settingSchema, so the UI
// doesn't have to hardcode them.
export const getRelatedListSetting = async (auth, moduleApiName) => {
  try {
    const resp = await axios.request({
      url: SETTINGS_URL,
      method: "GET",
      headers: buildHeaders(auth),
      params: { settingType: "relatedList", moduleApiName },
    });
    const all = extractList(resp);
    // Tolerant of camelCase / snake_case keys returned by the backend
    const row =
      all.find((s) => {
        const mod = s?.moduleApiName ?? s?.module_name ?? s?.module;
        const type = s?.settingType ?? s?.setting_type ?? s?.type;
        return (!moduleApiName || mod === moduleApiName) && (!type || type === "relatedList");
      }) || all[0] || null;

    if (!row) return null;

    const rawSchema = row.settingSchema ?? row.setting_schema ?? null;
    let schema = {};
    try {
      schema = typeof rawSchema === "string" ? JSON.parse(rawSchema) : rawSchema || {};
    } catch {
      schema = {};
    }
    return {
      settingId: row.settingId ?? row.setting_id ?? null,
      moduleApiName: row.moduleApiName ?? row.module_name ?? moduleApiName,
      settingSchema: schema,
    };
  } catch (error) {
    console.error("getRelatedListSetting error:", error);
    return null;
  }
};

// ── CREATE ──

// Create a new setting (settingId is left empty)
export const createSetting = async (auth, payload) => {
  try {
    const resp = await axios.request({
      url: SETTINGS_URL,
      method: "POST",
      headers: buildHeaders(auth),
      data: {
        settingId: "",
        settingSchema: payload?.settingSchema || {},
        showInRelatedList: payload?.showInRelatedList || false,
        moduleApiName: payload?.moduleApiName || "",
        settingType: payload?.settingType || "systemDefined",
      },
    });
    return resp?.data?.data || resp?.data || null;
  } catch (error) {
    console.error("createSetting error:", error);
    return null;
  }
};

// ── UPDATE ──

// Update an existing setting by id
export const updateSettingById = async (auth, settingId, payload) => {
  try {
    const resp = await axios.request({
      url: SETTINGS_URL,
      method: "POST",
      headers: buildHeaders(auth),
      data: {
        settingId,
        settingSchema: payload?.settingSchema || {},
        showInRelatedList: payload?.showInRelatedList || false,
        moduleApiName: payload?.moduleApiName || "",
        settingType: payload?.settingType || "systemDefined",
      },
    });
    return resp?.data?.data || resp?.data || null;
  } catch (error) {
    console.error("updateSettingById error:", error);
    return null;
  }
};

// ── UPSERT ──

// Upsert: create if no settingId, update if settingId is provided
export const upsertSetting = async (auth, payload) => {
  try {
    const resp = await axios.request({
      url: SETTINGS_URL,
      method: "POST",
      headers: buildHeaders(auth),
      data: {
        settingId: payload?.settingId || "",
        settingSchema: payload?.settingSchema || {},
        showInRelatedList: payload?.showInRelatedList || false,
        moduleApiName: payload?.moduleApiName || "",
        settingType: payload?.settingType || "systemDefined",
      },
    });
    return resp?.data?.data || resp?.data || null;
  } catch (error) {
    console.error("upsertSetting error:", error);
    return null;
  }
};

// ── BULK INACTIVATE ──

// Inactivate every setting tied to the store identified by the headers
// (orgid + connname + shopifyshopurl). Single backend call — used by the
// store-disconnect flow so the UI doesn't have to walk every setting.
// Note: this route lives under SERVICE_API_URL (the ecommerce service),
// not the admin/db settings path the rest of this file uses.
export const inactivateAllSettings = async (headers) => {
  try {
    const resp = await axios({
      method: "PUT",
      url: SERVICE_API_URL + "/settings/inactive",
      headers,
    });
    return resp?.data || true;
  } catch (error) {
    console.error("inactivateAllSettings error:", error);
    return null;
  }
};

// ── DELETE ──

// Delete all settings for the client
export const deleteAllSetting = async (auth) => {
  try {
    const resp = await axios.request({
      url: SETTINGS_URL,
      method: "DELETE",
      headers: buildHeaders(auth),
    });
    return resp?.data?.success ?? true;
  } catch (error) {
    console.error("deleteAllSetting error:", error);
    return false;
  }
};

// Delete a single setting by id
export const deleteSettingById = async (auth, settingId) => {
  try {
    const resp = await axios.request({
      url: `${SETTINGS_URL}/${settingId}`,
      method: "DELETE",
      headers: buildHeaders(auth),
    });
    return resp?.data?.success ?? true;
  } catch (error) {
    console.error("deleteSettingById error:", error);
    return false;
  }
};
